import {
  ITEToken,
  TCurrentUser,
  TOrganizationPublic,
} from "@timeedit/registration-schemas";
import { logger } from "@timeedit/registration-components";
import { mergeTokenAndUser, teApiSlice } from "../../redux/api/te";
import { AppDispatch } from "../../redux/store";

/**
 * Seeds RTK Query's user/org caches from Hub's federated `common/stores`,
 * keyed on `token` so consumers hit fulfilled entries instead of refetching.
 *
 * Never throws — any failure (Hub module missing, data absent or invalid,
 * unexpected error) resolves to `false` so the caller can fall back to its
 * own API fetch. Resolves `true` only when both caches were populated.
 */
export async function seedHubCache(
  dispatch: AppDispatch,
  token: ITEToken
): Promise<boolean> {
  try {
    // eslint-disable-next-line import/no-unresolved -- federated module from Hub remote
    const { userStore } = await import("common/stores");

    const { selfUser, organization } = userStore.getState();
    if (!selfUser || !organization) return false;

    const parsedUser = TCurrentUser.safeParse(selfUser);
    const parsedOrg = TOrganizationPublic.safeParse(organization);
    if (!parsedUser.success || !parsedOrg.success) {
      logger.warn(
        "[seedHubCache] Hub payload failed validation; skipping seed",
        {
          selfUser: parsedUser.success ? null : parsedUser.error.issues,
          organization: parsedOrg.success ? null : parsedOrg.error.issues,
        }
      );
      return false;
    }

    await Promise.all([
      dispatch(
        teApiSlice.util.upsertQueryData(
          "fetchTeUser",
          token,
          mergeTokenAndUser(token, parsedUser.data)
        )
      ),
      dispatch(
        teApiSlice.util.upsertQueryData(
          "fetchTeOrganization",
          { organizationId: token.organizationId },
          parsedOrg.data
        )
      ),
    ]);

    return true;
  } catch (error) {
    logger.warn("[seedHubCache] seeding failed; falling back to API fetch", {
      error,
    });
    return false;
  }
}
